import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Heart, ShieldCheck, Users, BookOpen, Menu, X, LifeBuoy, Shield, Instagram, HeartHandshake, Scale } from 'lucide-react';
import QuickExitButton from './QuickExitButton';
import GroundingFAB from './GroundingFAB';
import FloatingQuickExit from './FloatingQuickExit';
import AccessibilityFAB from './AccessibilityFAB';
import SkipToContent from './SkipToContent';
import LanguageToggle from './LanguageToggle';
import TranslateHelper from './TranslateHelper';
import CymraegPendingNotice from './CymraegPendingNotice';
import ShareCard from './ShareCard';
import { useLanguage } from '../i18n/LanguageContext';

const SV_PATHS = ['/app/sexual-violence', '/app/process', '/app/rights', '/app/trauma'];

const NAV_ITEMS = [
  { to: '/app', key: 'home', icon: Heart, labelKey: 'nav.home', fullKey: 'nav.home' },
  { to: '/app/sexual-violence', key: 'sv', icon: HeartHandshake, labelKey: 'nav.sv', fullKey: 'nav.sv.full', activeWhen: SV_PATHS },
  { to: '/app/domestic-abuse', key: 'da', icon: Shield, labelKey: 'nav.da', fullKey: 'nav.da.full' },
  { to: '/app/rights', key: 'rights', icon: ShieldCheck, labelKey: 'nav.rights', fullKey: 'nav.rights' },
  { to: '/app/consent', key: 'consent', icon: Scale, labelKey: 'nav.consent', fullKey: 'nav.consent' },
  { to: '/app/support', key: 'support', icon: Users, labelKey: 'nav.support', fullKey: 'nav.support' },
  { to: '/app/journal', key: 'journal', icon: BookOpen, labelKey: 'nav.journal', fullKey: 'nav.journal' },
  { to: '/app/trauma', key: 'trauma', icon: LifeBuoy, labelKey: 'nav.trauma', fullKey: 'nav.trauma' },
];

function isActive(item, pathname) {
  if (item.activeWhen) {
    return item.activeWhen.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  }
  return pathname === item.to || (item.to !== '/app' && pathname.startsWith(item.to));
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  return (
    <div className="min-h-screen" style={{ background: 'var(--tf-bg)' }}>
      <SkipToContent />
      {/* Top header */}
      <header
        className="sticky top-0 z-40 tf-no-print"
        role="banner"
        style={{
          background: 'rgba(253,252,254,0.92)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid var(--tf-card-border)',
        }}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between gap-3">
          <Link
            to="/"
            data-testid="brand-link"
            className="flex items-center gap-3 group"
            style={{ textDecoration: 'none' }}
          >
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{
                background:
                  'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
              }}
            >
              <span
                className="font-heading text-white text-lg"
                style={{ lineHeight: 1, paddingTop: 2 }}
              >
                F
              </span>
            </div>
            <div className="flex flex-col">
              <span
                className="font-body font-bold tracking-[0.18em]"
                style={{ fontSize: 11, color: 'var(--tf-purple-dark)' }}
              >
                THE FULL IMPACT
              </span>
              <span
                className="font-body hidden sm:block"
                style={{ fontSize: 10.5, color: 'var(--tf-text-muted)' }}
              >
                Sexual violence &amp; domestic abuse · Read in your browser
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav
            className="hidden md:flex items-center gap-1"
            aria-label={t('nav.aria.primary')}
          >
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isActive(item, pathname);
              const shortLabel = t(item.labelKey);
              const fullLabel = t(item.fullKey);
              const isAbbreviation = shortLabel !== fullLabel;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.key}`}
                  title={fullLabel}
                  aria-current={active ? 'page' : undefined}
                  aria-label={isAbbreviation ? fullLabel : undefined}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-bold transition-colors tf-focus"
                  style={{
                    color: active ? 'var(--tf-purple)' : 'var(--tf-text-secondary)',
                    background: active ? 'var(--tf-bg-secondary)' : 'transparent',
                    textDecoration: 'none',
                    minHeight: 40,
                  }}
                >
                  <Icon size={14} strokeWidth={2} aria-hidden="true" focusable="false" />
                  <span aria-hidden={isAbbreviation ? 'true' : undefined}>{shortLabel}</span>
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden md:block">
              <LanguageToggle />
            </div>
            <TranslateHelper />
            <QuickExitButton />
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center rounded-full tf-focus"
              style={{
                background: 'var(--tf-bg-secondary)',
                color: 'var(--tf-text)',
                width: 44,
                height: 44,
                flexShrink: 0,
              }}
              onClick={() => setOpen(!open)}
              aria-label={open ? t('btn.menu.close') : t('btn.menu.open')}
              aria-expanded={open}
              aria-controls="mobile-nav-drawer"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={18} aria-hidden="true" focusable="false" /> : <Menu size={18} aria-hidden="true" focusable="false" />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div
            id="mobile-nav-drawer"
            className="md:hidden border-t"
            style={{ borderColor: 'var(--tf-card-border)', background: '#fff' }}
          >
            <nav
              className="px-5 py-3 flex flex-col gap-1"
              aria-label={t('nav.aria.mobile')}
            >
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const active = isActive(item, pathname);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`mobile-nav-${item.key}`}
                    onClick={() => setOpen(false)}
                    aria-current={active ? 'page' : undefined}
                    className="inline-flex items-center gap-3 px-3 py-3 rounded-xl text-[14px] font-bold tf-focus"
                    style={{
                      color: active ? 'var(--tf-purple)' : 'var(--tf-text)',
                      background: active ? 'var(--tf-bg-secondary)' : 'transparent',
                      textDecoration: 'none',
                      minHeight: 44,
                    }}
                  >
                    <Icon size={16} aria-hidden="true" focusable="false" />
                    {t(item.fullKey)}
                  </Link>
                );
              })}
              <div className="pt-3 mt-2" style={{ borderTop: '1px solid var(--tf-card-border)' }}>
                <LanguageToggle onSwitch={() => setOpen(false)} />
              </div>
            </nav>
          </div>
        )}
      </header>

      <main id="main-content" className="pb-24 md:pb-12" tabIndex={-1}>
        {pathname !== '/app/accessibility' && (
          <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-6">
            <CymraegPendingNotice />
          </div>
        )}
        {children}
      </main>

      {/* Footer */}
      <footer
        className="border-t tf-no-print"
        style={{ borderColor: 'var(--tf-card-border)', background: 'var(--tf-bg-secondary)' }}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid sm:grid-cols-3 gap-6">
          <div>
            <p className="eyebrow">SOURCED FROM</p>
            <p className="font-body text-[12.5px] mt-2" style={{ color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
              Rape Crisis England &amp; Wales · CPS · Victims&apos; Code of Practice · gov.uk · NHS · Mind · PTSD UK
            </p>
          </div>
          <div>
            <p className="eyebrow">IN URGENT NEED</p>
            <p className="font-body text-[12.5px] mt-2" style={{ color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}>
              Call <a href="tel:999" className="tf-link">999</a> in immediate danger ·{' '}
              <a href="tel:116123" className="tf-link">116 123</a> Samaritans ·{' '}
              <a href="tel:08088029999" className="tf-link">0808 802 9999</a> Rape Crisis
            </p>
          </div>
          <div>
            <p className="eyebrow">DISCLAIMER</p>
            <p className="font-body text-[12px] mt-2" style={{ color: 'var(--tf-text-muted)', lineHeight: 1.6 }}>
              The Full Impact provides general information for survivors of rape, sexual violence and domestic abuse in the UK. It is not legal advice. For legal advice please contact a solicitor or your ISVA / IDVA.
            </p>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-4 flex justify-center">
          <a
            href="https://www.instagram.com/thefullimpactcampaign?igsh=cHEyd2Jjbm9jMjhx"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold"
            style={{
              background: '#fff',
              color: 'var(--tf-purple-dark)',
              border: '1px solid var(--tf-purple-light)',
              fontSize: 13,
              textDecoration: 'none',
            }}
          >
            <Instagram size={15} />
            Follow @thefullimpactcampaign
          </a>
        </div>

        {/* Share this site card — for ISVAs, IDVAs, social workers */}
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pb-8">
          <ShareCard />
        </div>

        {/* Copyright / footer meta */}
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-8 text-center">
          <p className="font-body" style={{ fontSize: 11.5, color: 'var(--tf-text-muted)', lineHeight: 1.6 }}>
            © {new Date().getFullYear()} <strong style={{ color: 'var(--tf-text-secondary)' }}>Angela Moreton</strong>. All rights reserved.
            <br className="sm:hidden" />
            <span className="hidden sm:inline"> · </span>
            The Full Impact™ — a trauma-informed survivor guide for the UK.
          </p>
          <p
            className="font-body mt-2 mx-auto"
            style={{ fontSize: 10.5, color: 'var(--tf-text-muted)', lineHeight: 1.6, maxWidth: 720 }}
          >
            Original writing, design and selection of resources by Angela Moreton. Content licensed under{' '}
            <a
              href="https://creativecommons.org/licenses/by-nc-nd/4.0/"
              target="_blank"
              rel="noopener noreferrer"
              className="tf-link"
            >
              CC BY-NC-ND 4.0
            </a>
            {' '}— you may share it freely with attribution, but not commercially and not in modified form. Quoted
            material from Rape Crisis, CPS, NHS, gov.uk and other UK sources remains the copyright of its respective owners.
          </p>
          <p className="font-body mt-2" style={{ fontSize: 10.5, color: 'var(--tf-text-muted)' }}>
            {t('footer.line1')} · {' '}
            <Link to="/app/why" data-testid="footer-why-link" className="tf-link">{t('footer.why')}</Link>{' '}·{' '}
            <Link to="/app/about" data-testid="footer-about-link" className="tf-link">{t('footer.about')}</Link>{' '}·{' '}
            <Link to="/app/accessibility" data-testid="footer-a11y-link" className="tf-link">{t('footer.accessibility')}</Link>{' '}·{' '}
            <Link to="/app/compliance" data-testid="footer-compliance-link" className="tf-link">Compliance summary</Link>{' '}·{' '}
            <Link to="/app/faq" data-testid="footer-faq-link" className="tf-link">{t('footer.faq')}</Link>
          </p>
        </div>
      </footer>

      {/* Mobile bottom tab bar */}
      <nav
        className="md:hidden tf-tabbar tf-no-print fixed bottom-0 left-0 right-0 z-30"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        aria-label={t('nav.aria.tabbar')}
      >
        <div className="grid grid-cols-5 px-1 py-1.5">
          {NAV_ITEMS.filter((n) => ['/app', '/app/sexual-violence', '/app/domestic-abuse', '/app/support', '/app/journal'].includes(n.to)).map((item) => {
            const Icon = item.icon;
            const active = isActive(item, pathname);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`tab-${item.key}`}
                aria-current={active ? 'page' : undefined}
                aria-label={t(item.fullKey)}
                className="flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-xl tf-focus"
                style={{
                  color: active ? 'var(--tf-purple)' : 'var(--tf-text-muted)',
                  textDecoration: 'none',
                  minHeight: 44,
                }}
              >
                <Icon size={20} strokeWidth={active ? 2.2 : 1.8} aria-hidden="true" focusable="false" />
                <span className="font-body" style={{ fontSize: 10.5, fontWeight: active ? 700 : 600 }}>
                  {t(item.labelKey)}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      <GroundingFAB />
      <FloatingQuickExit />
      <AccessibilityFAB />
    </div>
  );
}
